import { useEffect, useState } from "react";
import { ThumbnailCropper } from "./ThumbnailCropper";

/**
 * Picker + cropper combo. The editor picks a file (drag, click, or paste);
 * we open the cropper modal; on accept, we hand the JPEG Blob upstream.
 *
 * Two modes:
 *   - "uploaded": already-uploaded thumbnail URL is shown as the preview.
 *   - "draft": a Blob the editor just cropped, not yet committed to YT.
 *
 * The parent is responsible for actually uploading the Blob (different
 * timing for upload-page vs. edit-page flows).
 */

interface Props {
  /** Currently committed thumbnail URL (e.g. from YouTube). */
  uploadedUrl?: string | null;
  /** Most recent unsaved Blob (set by the cropper). */
  draftBlob?: Blob | null;
  /** Called when the editor accepts a new crop. */
  onCrop: (cropped: Blob) => void;
  /** Called when the editor clicks "Remove" (drops the draft, doesn't
   *  delete the YT thumb — YouTube has no API for that). */
  onClearDraft?: () => void;
}

export function ThumbnailPicker({
  uploadedUrl,
  draftBlob,
  onCrop,
  onClearDraft,
}: Props) {
  const [pickedSource, setPickedSource] = useState<File | Blob | null>(null);
  const [draftPreview, setDraftPreview] = useState<string | null>(null);

  useEffect(() => {
    if (!draftBlob) {
      setDraftPreview(null);
      return;
    }
    const url = URL.createObjectURL(draftBlob);
    setDraftPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [draftBlob]);

  const onPick = (file: File) => {
    if (!file.type.startsWith("image/")) {
      window.alert("Please pick an image (JPEG, PNG, or WebP).");
      return;
    }
    setPickedSource(file);
  };

  const previewUrl = draftPreview ?? uploadedUrl ?? null;

  return (
    <div className="yt-thumb-picker">
      <div className="yt-thumb-frame">
        {previewUrl ? (
          <img src={previewUrl} alt="" loading="lazy" />
        ) : (
          <span className="yt-thumb-empty font-display-italic">
            No thumbnail yet
          </span>
        )}
      </div>
      <div className="yt-thumb-actions">
        <label className="btn-app-ghost cursor-pointer">
          <span>{previewUrl ? "Replace" : "Choose image"}</span>
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="sr-only"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onPick(f);
              e.target.value = ""; // reset so picking the same file re-fires
            }}
          />
        </label>
        {draftBlob && onClearDraft && (
          <button
            type="button"
            className="row-action"
            onClick={onClearDraft}
          >
            Discard crop
          </button>
        )}
        <p className="font-grotesk text-[0.62rem] text-[var(--color-ink-mute)] tracking-[0.04em]">
          1280×720 minimum · JPEG / PNG / WebP · ≤ 2 MB after crop
        </p>
      </div>
      {pickedSource && (
        <ThumbnailCropper
          source={pickedSource}
          open
          onClose={() => setPickedSource(null)}
          onAccept={(blob) => {
            onCrop(blob);
            setPickedSource(null);
          }}
        />
      )}
    </div>
  );
}
